import {action, makeAutoObservable, observable, reaction} from 'mobx'
import {selectedCompanyStore} from "./SelectedCompany";
import {localStore} from "./localStore";
import {makePersistable, stopPersisting} from "mobx-persist-store";
import {dateGetter, dateSetter} from "../utils/DataFormat";
import {Nil} from "../utils/Types";
import {StorageController} from "mobx-persist-store/lib/esm2017/types";


const properties: (keyof NewEmployeeWizardState)[] = [
    'companyId',
    'performsWorkInEstonia',
    'name',
    'surname',
    'personalCode',
    'residentialAddress',
    'allowedToWorkByCitizenship',
    'workPermissionType',
    'contractType',
    'position',
    'workAddress',
    'rateType',
    'rateValue',
    'worksFullTime',
    'partTimeAllocationValue',
    '_contractStartDate',
    'contractHasEndDate',
    '_contractEndDate',
    'probationPeriod',
    'probationPeriodMonths',
    'taxResidenceCountryType',
    'taxResidenceCountryValue',
    'taxDeductionNeeded',
    'shouldWeRegisterWorker',
    'contractTemplateNeeded',
    'countersigningPersonFullName',
    'dayOfPayment',
    'employeeIban',
    'employeeDutiesDescription'
];

export enum ContractType {
    EMPLOYMENT = "EMPLOYMENT",
    BOARD_MEMBER = "BOARD_MEMBER",
}

export enum WorkPermissionType {
    PERM_RESIDENCE_PERMIT = "PERM_RESIDENCE_PERMIT",
    TEMP_RESIDENCE_PERMIT = "TEMP_RESIDENCE_PERMIT",
    SHORT_TERM_PERMISSION = "SHORT_TERM_PERMISSION",
    DONT_KNOW = "DONT_KNOW",
}

export enum TaxResidenceCountryType {
    ESTONIA = "ESTONIA",
    OTHER = "OTHER",
    DONT_KNOW = "DONT_KNOW",
}

export enum RateType {
    HOURLY = "HOURLY",
    MONTHLY = "MONTHLY",
}

export class NewEmployeeWizardState {
    @observable companyId?: string

    @observable performsWorkInEstonia?: boolean
    //See com.nola.services.employee.NewEmployeeService.startUploadSession
    @observable name?: string
    @observable surname?: string
    @observable personalCode?: string
    @observable residentialAddress?: string

    @observable allowedToWorkByCitizenship?: boolean
    @observable workPermissionType?: WorkPermissionType

    @observable contractType?: ContractType
    @observable position?: string
    @observable workAddress?: string
    @observable rateType?: RateType
    @observable rateValue?: string
    @observable worksFullTime?: boolean
    @observable partTimeAllocationValue?: string
    @observable _contractStartDate?: string
    @observable contractHasEndDate?: boolean
    @observable _contractEndDate?: string
    @observable probationPeriod?: boolean
    @observable probationPeriodMonths?: string

    @observable taxResidenceCountryType?: TaxResidenceCountryType
    @observable taxResidenceCountryValue?: string | null
    @observable taxDeductionNeeded?: boolean
    @observable shouldWeRegisterWorker?: boolean

    @observable contractTemplateNeeded?: boolean
    @observable countersigningPersonFullName?: string
    @observable dayOfPayment?: string
    @observable employeeIban?: string
    @observable employeeDutiesDescription?: string

    //Non persistable
    @observable document?: File | Nil
    @observable workPermitDocument?: File | Nil
    @observable taxResidenceStatementDocument?: File | Nil
    @observable taxDeductionFormDocument?: File | Nil
    @observable workContractDocument?: File | Nil


    constructor(store: StorageController | Nil = localStore) {
        if (store) {
            makeAutoObservable(this);
            makePersistable(this, {
                name: 'newEmployeeWizardState',
                properties: properties,
                storage: store
            }).then(() => {
                this.setDefaults();
                console.log("NewEmployeeWizardState hydrated")
            });
        }
    }

    public stopPersisting = () => {
        stopPersisting(this)
    }

    private setDefaults = () => {
        if (this.dayOfPayment === undefined) {
            this.dayOfPayment = "10";
        }
        if (this.contractTemplateNeeded === undefined) {
            this.contractTemplateNeeded = false;
        }
    }

    @action
    public resetState = (companyId?: string) => {
        if (this.companyId !== companyId) {
            properties.forEach(it => {
                // @ts-ignore
                this[it] = undefined
            })
            this.setDefaults()
            this.companyId = companyId ?? selectedCompanyStore.companyId;
        }
    }

    get contractStartDate() {
        return dateGetter(this._contractStartDate)
    }

    set contractStartDate(date) {
        this._contractStartDate = dateSetter(date)
    }

    get contractEndDate() {
        return dateGetter(this._contractEndDate)
    }

    set contractEndDate(date) {
        this._contractEndDate = dateSetter(date)
    }

    public restoreFrom = (obj: any) => {
        for (const attribut in obj) {
            // @ts-ignore
            this[attribut] = obj[attribut]
        }
    }
}

export const newEmployeeWizardState = new NewEmployeeWizardState();

export const createStateInstance = (obj: any) => {
    const state = new NewEmployeeWizardState(undefined);
    state.restoreFrom(obj)
    return state;
}

reaction(
        () => selectedCompanyStore.companyId,
        (companyId) => newEmployeeWizardState.resetState(companyId)
)
